import { useEffect, useRef } from 'react';

import { isDefined, isNotDefined } from '@enterprise/core';

import type { ScrollToEdgeContext } from './types/scroll-to-edge-context.type';
import type { UseScrollToEdgesOptions } from './types/use-scroll-to-edges.options';

/**
 * React hook that detects when a scrollable container reaches its top or bottom edge.
 *
 * Attaches a scroll event listener to the provided container element and triggers
 * the specified callbacks when the scroll position is within the given threshold
 * of the top or bottom edge. Uses requestAnimationFrame for efficient scroll event handling.
 *
 * @param containerRef - Ref object pointing to the scrollable container element.
 * @param options - Configuration options:
 *   - onScrollToBottom: Callback invoked when scrolled to the bottom edge.
 *   - onScrollToTop: Callback invoked when scrolled to the top edge.
 *   - threshold: Pixel threshold for detecting proximity to the edge (default: 1).
 *
 * @remarks
 * - Only triggers callbacks when the edge is reached for the first time (debounced).
 * - Cleans up event listeners and animation frames on unmount.
 */
export const useScrollToEdges = (
  containerRef: React.RefObject<HTMLElement | null>,
  { onScrollToBottom, onScrollToTop, threshold = 1 }: UseScrollToEdgesOptions
) => {
  const rafIdRef = useRef<ReturnType<typeof requestAnimationFrame> | null>(null);
  const lastScrollTopRef = useRef(0);
  const tickingRef = useRef(false);
  const wasAtBottomRef = useRef(false);
  const wasAtTopRef = useRef(false);

  useEffect(() => {
    const element = containerRef.current;

    if (isNotDefined(element)) {
      return;
    }

    const onScroll = () => {
      if (tickingRef.current) {
        return;
      }

      tickingRef.current = true;

      rafIdRef.current = requestAnimationFrame(() => {
        const { clientHeight, scrollHeight, scrollTop } = element;
        const scrollingDown = scrollTop > lastScrollTopRef.current;
        const scrollingUp = scrollTop < lastScrollTopRef.current;

        const atTop = scrollTop <= threshold;
        const atBottom = Math.abs(scrollHeight - scrollTop - clientHeight) <= threshold;

        const context: ScrollToEdgeContext = {
          atBottom,
          atTop,
          clientHeight,
          scrollHeight,
          scrollTop,
        };

        lastScrollTopRef.current = scrollTop;
        tickingRef.current = false;

        if (isDefined(onScrollToTop)) {
          if (atTop && scrollingUp && !wasAtTopRef.current) {
            wasAtTopRef.current = true;
            onScrollToTop(context);
          } else if (!atTop) {
            wasAtTopRef.current = false;
          }
        }

        if (isDefined(onScrollToBottom)) {
          if (atBottom && scrollingDown && !wasAtBottomRef.current) {
            wasAtBottomRef.current = true;
            onScrollToBottom(context);
          } else if (!atBottom) {
            wasAtBottomRef.current = false;
          }
        }
      });
    };

    element.addEventListener('scroll', onScroll);

    return () => {
      element.removeEventListener('scroll', onScroll);

      if (isDefined(rafIdRef.current)) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [containerRef, onScrollToBottom, threshold, onScrollToTop]);
};
